<template>
  <app-floating
    v-model="isExpanded"
    placement="bottom-end"
    :floating-style="{ width: 'unset' }"
    control="manual"
    @hide-floating="onHideFloating"
  >
    <template #content>
      <button class="notification-indicator" @click="onToggleFloating">
        <fa-icon-compat
          class="notification-indicator-icon"
          icon="fa-regular fa-bell"
        />
        <fa-icon-compat
          v-if="hasUnread"
          class="icon notification-indicator-dot"
          icon="fa-solid fa-circle"
        />
      </button>
    </template>

    <template #options>
      <notification-inbox
        id="notification-inbox"
        :emptyResource="emptyResource"
        :notifications="notifications"
        @clickNotification="clickNotification"
        @deleteNotification="deleteNotification"
        @markNotificationRead="markAsRead"
        @markAllAsRead="markAllAsRead"
      />
    </template>
  </app-floating>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { ContentType } from '@rready/sdk';
import type {
  IWorkflowV2,
  IEmptyCard,
  INotification,
  INotificationModalResource
} from '@rready/sdk';
import { useViewSettings } from '@rready/composables';
import NotificationInbox from './NotificationInbox.vue';
import { useProductStore } from '../../store/productStore';

const NOTIFICATIONS_DEBOUNCE_DELAY = 4000;

export default defineComponent({
  name: 'NotificationIndicator',
  components: {
    NotificationInbox
  },
  data() {
    return {
      notifications: [] as INotification[],
      isExpanded: false,
      loadNotificationDebounced: undefined as undefined | (() => Promise<void>)
    };
  },
  async created(): Promise<void> {
    this.loadNotificationDebounced = useDebounceFn(async () => {
      await this.loadNotifications();
      await this.checkAnnouncements();
    }, NOTIFICATIONS_DEBOUNCE_DELAY);
    this.loadNotificationDebounced();
  },
  watch: {
    $route() {
      this.onHideFloating();
      this.loadNotificationDebounced?.();
    }
  },
  computed: {
    hasUnread(): boolean {
      return this.notifications.some((n) => !n.readAt);
    },
    workflow(): IWorkflowV2 {
      return useProductStore().workflow;
    },
    emptyResource(): IEmptyCard {
      const pageSettings = useViewSettings(this.workflow, 'app').page(
        'Notifications'
      );
      return this.$res<any>(pageSettings?.resources)?.empty ?? {};
    }
  },
  methods: {
    onToggleFloating() {
      this.loadNotifications();
      this.isExpanded = !this.isExpanded;
    },
    onHideFloating() {
      this.isExpanded = false;
    },
    notificationResource(
      notification: INotification
    ): INotificationModalResource {
      const resource = JSON.parse(notification.body);
      return this.$res(resource);
    },
    checkAnnouncements() {
      const announcement = this.notifications.find(
        (n) => n.meta?.announcement && !n.readAt
      );
      if (announcement) {
        this.clickNotification(announcement);
      }
    },
    clickNotification(notification: INotification): void {
      this.markAsRead(notification);
      const notificationResource = this.notificationResource(notification);
      if (notification.contentType === ContentType.MODAL) {
        this.$modal('basic', notificationResource);
      } else if (notificationResource.notificationLink) {
        window.location.href = notificationResource.notificationLink;
      }
      this.onHideFloating();
    },
    async loadNotifications() {
      const unsortedNotifications: INotification[] =
        await this.$gateway.portal.getMessages();
      this.notifications = unsortedNotifications.sort(
        (a: INotification, b: INotification) => b.createdAt - a.createdAt
      );
    },
    async deleteNotification(notification: INotification): Promise<void> {
      try {
        this.$api.notifications.deleteMessage(notification.id);
      } catch (e) {
        console.error(e);
      }
      this.notifications = this.notifications.filter(
        (n) => n.id !== notification.id
      );
    },
    async markAsRead(notification: INotification): Promise<void> {
      await this.$api.notifications.markAsRead(notification.id);
      this.notifications = this.notifications.map((n) => {
        if (n.id === notification.id) {
          return {
            ...n,
            readAt: 1 // Just need a truthy value
          };
        }
        return n;
      });
    },
    async markAllAsRead(): Promise<void> {
      const targets = this.notifications
        .filter((n) => !n.readAt)
        .map((n) => n.id);
      await this.$api.notifications.markAsReadBatch(targets);
      await this.loadNotifications();
    }
  }
});
</script>

<style lang="scss" scoped>
.notification-indicator {
  position: relative;
  display: grid;
  place-content: center;
  width: 36px;
  height: 40px;
  cursor: pointer;

  .notification-indicator-icon {
    font-size: 19px;
    font-weight: 400;
    color: var(--color-grey-dark);
  }

  .notification-indicator-dot {
    position: absolute;
    top: 3px;
    right: 5px;
    color: var(--color-blue);
    font-size: 7px;
  }
}
</style>
